import { DataService } from "@/services/data";
import { apiErrorMessage } from "@/utils/api-error";
import { ApprenticeshipRegistrationRelatedType, ApprenticeshipRegistrationType } from "@/types/apprenticeship/apprentice";
import { handle_error, handle_success } from "@/utils/wretch";
import { Navigate, RouteObject, defer, json, redirect } from "react-router-dom";
import { toast } from "sonner";
import { LMSDataService } from "@/services/lms_data";
import { ApprenticeshipTaskType } from "@/types/apprenticeship/task";
import { PaginatedResponse } from "@/types/common";
import { ApprenticeshipAppointmentType } from "@/types/apprenticeship/enterprise";

const ApprenticeshipStudentRoute: RouteObject = {
    path: "etudiants",
    children: [
        {
            index: true,
            async lazy() {
                let { ListPage } = await import("@/pages/apprenticeship/student/list");
                return { Component: ListPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                url.searchParams.set("is_student", "1");

                const search_param = url.searchParams.get("search");
                if (search_param) {
                    url.searchParams.set("smart_search", search_param);
                    url.searchParams.delete("search");
                }

                const registrations = DataService.client
                    .url(`/apprenticeship/registrations/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return defer({ registrations });
            }
        },
        {
            path: "onboarding",
            async lazy() {
                let { OnboardingCalendarLayout } = await import("@/pages/apprenticeship/student/onboarding-layout");
                return { Component: OnboardingCalendarLayout };
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="calendrier" replace />
                },
                {
                    path: "calendrier",
                    loader: async ({ request }) => {
                        const { DEFAULT_DATE_RANGE } = await import("@/pages/apprenticeship/student/onboarding-calendar");
                        const url = new URL(request.url);
                        if (!url.searchParams.has("dt_start__range")) {
                            url.searchParams.set("dt_start__range", DEFAULT_DATE_RANGE);
                        }
                        url.searchParams.set("event_type__in", "onboarding,onboarding_enterprise,tri_partite_interview");
                        url.searchParams.set("page_size", "1000");
                        const appointments = await DataService.client
                            .url(`/apprenticeship/appointments/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json();
                        return defer({ appointments });
                    },
                    async lazy() {
                        let { OnboardingCalendarPage } = await import("@/pages/apprenticeship/student/onboarding-calendar");
                        return { Component: OnboardingCalendarPage };
                    }
                },
                {
                    path: "liste",
                    loader: async ({ request }) => {
                        const { DEFAULT_DATE_RANGE } = await import("@/pages/apprenticeship/student/onboarding-list");
                        const url = new URL(request.url);
                        if (!url.searchParams.has("dt_start__range")) {
                            url.searchParams.set("dt_start__range", DEFAULT_DATE_RANGE);
                        }
                        url.searchParams.set("event_type__in", "onboarding,onboarding_enterprise,tri_partite_interview");
                        url.searchParams.set("page_size", "1000");
                        url.searchParams.set("o", "dt_start");
                        const appointments = await DataService.client
                            .url(`/apprenticeship/appointments/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json();
                        return defer({ appointments });
                    },
                    async lazy() {
                        let { OnboardingListPage } = await import("@/pages/apprenticeship/student/onboarding-list");
                        return { Component: OnboardingListPage };
                    }
                }
            ]
        },
        {
            id: "apprenticeship-student-detail",
            path: ":registrationId",
            async lazy() {
                let { DetailLayout } = await import("@/pages/apprenticeship/student/detail-layout");
                return { Component: DetailLayout };
            },
            loader: async ({ params }) => {
                const registrationId = params.registrationId;
                const registration = (await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).get().json()) as ApprenticeshipRegistrationType;
                const picklists = await DataService.client.url(`/apprenticeship/registrations/picklists/?contract_type=${registration.apprenticeship_type}`).get().json();
                const appointments = (await DataService.client
                    .url(`/apprenticeship/appointments/?student_id=${registrationId}&page_size=1000`)
                    .get()
                    .json()) as PaginatedResponse<ApprenticeshipAppointmentType>;

                const tasks = DataService.client.url(`/apprenticeship/tasks/?registration_id=${registrationId}&page_size=1000`).get().json();
                return { registration, picklists, tasks, appointments: appointments.results ?? [] };
            },
            action: async ({ params, request }) => {
                const registrationId = params.registrationId;
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                if (action) {
                    try {
                        const content = await DataService.client.url(`/apprenticeship/registrations/${registrationId}/${action}/`).post(data).json();
                        return { success: true, content: content };
                    } catch (error) {
                        return handle_error(error);
                    }
                }
                return {};
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="dossier" replace />
                },
                {
                    path: "dossier",
                    async lazy() {
                        let { DossierPage } = await import("@/pages/apprenticeship/student/detail-dossier");
                        return { Component: DossierPage };
                    },
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const data = await request.json();
                        try {
                            await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).patch(data).json();
                        } catch (error) {
                            console.error(error);
                            toast.error("Une erreur est survenue lors de l'enregistrement du dossier");
                        }
                        return redirect("..");
                    }
                },
                {
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const appointments = await DataService.client.url(`/apprenticeship/appointments/?student_id=${registrationId}&page_size=1000`).get().json();
                        return { appointments };
                    },
                    path: "rendez-vous",
                    async lazy() {
                        let { AppointmentsPage } = await import("@/pages/apprenticeship/student/detail-appointments");
                        return { Component: AppointmentsPage };
                    }
                },
                {
                    id: "apprenticeship-student-factures",
                    path: "factures",
                    async lazy() {
                        let { FacturesPage } = await import("@/pages/apprenticeship/student/detail-factures");
                        return { Component: FacturesPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const paymentPlanItems = await DataService.client.url(`/apprenticeship/payments/?student_id=${registrationId}&page_size=1000`).get().json();
                        const invoice = await DataService.client.url(`/apprenticeship/registrations/${registrationId}/rac_invoice/`).get().json();
                        const sf_case = await DataService.client.url(`/apprenticeship/registrations/${registrationId}/sf_case/`).get().json();
                        // OPCO EP automatic invoicing: optional, the page works without it.
                        const convergence = await DataService.client
                            .url(`/apprenticeship/convergence-billing/${registrationId}/`)
                            .get()
                            .json()
                            .catch(() => null);
                        return { paymentPlanItems, invoice, sf_case, convergence };
                    },
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const data = await request.json();
                        if (request.method === "POST") {
                            try {
                                await DataService.client
                                    .url(`/apprenticeship/payments/`)
                                    .post({ ...data, student: registrationId })
                                    .json();
                            } catch (error) {
                                console.error(error);
                                toast.error(apiErrorMessage(error, "Une erreur est survenue lors de l'enregistrement de l'échéance"));
                            }
                        } else {
                            try {
                                await DataService.client
                                    .url(`/apprenticeship/payments/${data.id}/`)
                                    .put({ ...data, student: registrationId })
                                    .json();
                            } catch (error) {
                                console.error(error);
                                toast.error(apiErrorMessage(error, "Une erreur est survenue lors de la mise à jour de l'échéance"));
                            }
                        }
                        return redirect(".");
                    }
                },
                {
                    id: "apprenticeship-student-formation",
                    path: "formation",
                    async lazy() {
                        let { FormationPage } = await import("@/pages/apprenticeship/student/detail-formation");
                        return { Component: FormationPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const registration = (await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).get().json()) as ApprenticeshipRegistrationType;
                        const planningStudents = await LMSDataService.client.url(`/apprenticeship/admin/plannings/students/?search=${registration.email}`).get().json();
                        const sf_case = await DataService.client.url(`/apprenticeship/registrations/${registrationId}/sf_case/`).get().json();
                        return defer({ registration, planningStudents, sf_case });
                    }
                },
                {
                    id: "apprenticeship-student-interactions",
                    path: "interactions",
                    async lazy() {
                        let { InteractionsPage } = await import("@/pages/apprenticeship/student/detail-interactions");
                        return { Component: InteractionsPage };
                    },
                    loader: async ({ params, request }) => {
                        const url = new URL(request.url);
                        const registrationId = params.registrationId;

                        if (!url.searchParams.has("registration_id")) {
                            url.searchParams.set("registration_id", `${registrationId}`);
                        }

                        if (!url.searchParams.has("type")) {
                            url.searchParams.set("type__in", "call,visio");
                        }

                        if (!url.searchParams.has("page_size")) {
                            url.searchParams.set("page_size", "100");
                        }
                        const registration = (await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).get().json()) as ApprenticeshipRegistrationType;
                        const tasks = (await DataService.client
                            .url(`/apprenticeship/tasks/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json()) as PaginatedResponse<ApprenticeshipTaskType>;
                        return { tasks, registration };
                    }
                },
                {
                    path: "suivi-tuteur",
                    async lazy() {
                        let { TutorMonitoringPage } = await import("@/pages/apprenticeship/student/detail-tutor-monitoring");
                        return { Component: TutorMonitoringPage };
                    }
                },
                {
                    path: "questionnaires",
                    async lazy() {
                        // TODO: Change
                        let { SurveyPage } = await import("@/pages/apprenticeship/student/detail-survey");
                        return { Component: SurveyPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const surveys = await DataService.client.url(`/apprenticeship/surveys/?registration_id=${registrationId}&page_size=1000`).get().json();
                        return defer({ surveys });
                    },
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const url = new URL(request.url);
                        const data = await request.json();
                        if (url.searchParams.get("action")) {
                            try {
                                const response = await DataService.client
                                    .url(`/apprenticeship/surveys/${data.id}/${url.searchParams.get("action")}/`)
                                    .post(data)
                                    .json();
                                return json({ success: true, content: response });
                            } catch (error) {
                                return handle_error(error);
                            }
                        } else if (request.method === "POST") {
                            try {
                                await DataService.client
                                    .url(`/apprenticeship/surveys/`)
                                    .post({ ...data, registration: registrationId })
                                    .json();
                            } catch (error) {
                                console.error(error);
                                toast.error("Une erreur est survenue lors de la création du questionnaire");
                            }
                        } else {
                            try {
                                await DataService.client
                                    .url(`/apprenticeship/surveys/${data.id}/`)
                                    .put({ ...data, student: registrationId })
                                    .json();
                            } catch (error) {
                                console.error(error);
                                toast.error("Une erreur est survenue lors de la mise à jour du questionnaire");
                            }
                        }
                        return redirect(".");
                    }
                },
                {
                    id: "apprenticeship-student-related",
                    path: "related",
                    loader: async ({ params }) => {
                        try {
                            const registrationId = params.registrationId;
                            const data = (await DataService.client.url(`/apprenticeship/registrations/${registrationId}/related_registration/`).get().json()) as {
                                related_registration: ApprenticeshipRegistrationRelatedType | null;
                            };
                            return handle_success(data.related_registration);
                        } catch (error) {
                            return handle_error(error);
                        }
                    }
                }
            ]
        }
    ]
};

export default ApprenticeshipStudentRoute;
